import React from "react";
import TrebbleSegmented from "../common/TrebbleSegmented";
import TrebbleBreadcrumb from "../common/TrebbleBreadcrumb";
import type { TrebbleSegmentedOnChangeValue } from "../common/TrebbleSegmented";

interface Folder {
  id: string;
  name: string;
  color: string;
  itemCount: number;
}

interface LibraryToolbarProps {
  activeFolder: Folder | null;
  folderPath?: Folder[];
  onNavigateRoot: () => void;
  onNavigateToFolder?: (folder: Folder) => void;
  viewMode: "grid" | "list";
  onViewModeChange: (mode: "grid" | "list") => void;
  ti18n?: Record<string, string>;
  rootLabel?: string;
  showBreadcrumb?: boolean;
}

const t = (ti18n: Record<string, string> | undefined, key: string): string => {
  if (ti18n) {
    return window.getI18n(ti18n, key);
  }
  return key;
};

const SEG_ICON_STYLE: React.CSSProperties = {
  fontSize: "var(--text-lg)",
  WebkitTextStroke: "0.5px",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

const LibraryToolbar: React.FC<LibraryToolbarProps> = ({
  activeFolder,
  folderPath = [],
  onNavigateRoot,
  onNavigateToFolder,
  viewMode,
  onViewModeChange,
  ti18n,
  rootLabel,
  showBreadcrumb = true,
}) => {
  const handleViewModeChange = (value: TrebbleSegmentedOnChangeValue) => {
    onViewModeChange(value as "grid" | "list");
  };

  const resolvedRootLabel = rootLabel ?? t(ti18n, "MY_LIBRARY");

  const buildBreadcrumbItems = () => {
    if (!activeFolder) {
      return [{
        title: (
          <span>{resolvedRootLabel}</span>
        ),
      }];
    }

    const items: Array<{ title: React.ReactNode }> = [
      {
        title: (
          <a
            href="#"
            onClick={(e: React.MouseEvent) => {
              e.preventDefault();
              onNavigateRoot();
            }}
            style={{ color: "var(--text-default-color, #333)" }}
          >
            {resolvedRootLabel}
          </a>
        ),
      },
    ];

    // Add ancestor folders from the path (excluding the current folder)
    folderPath.forEach((folder) => {
      if (folder.id !== activeFolder.id) {
        const innerContent = (
          <>
            <span className="pe-7s-folder" style={{ fontSize: "var(--text-base)", color: folder.color, WebkitTextStroke: "0.5px" }} />
            <span>{folder.name}</span>
          </>
        );
        const sharedStyle: React.CSSProperties = { display: "inline-flex", alignItems: "center", gap: 4, color: "var(--text-default-color, #333)" };

        items.push({
          title: onNavigateToFolder ? (
            <a
              href="#"
              onClick={(e: React.MouseEvent) => {
                e.preventDefault();
                onNavigateToFolder(folder);
              }}
              style={sharedStyle}
            >
              {innerContent}
            </a>
          ) : (
            <span style={sharedStyle}>
              {innerContent}
            </span>
          ),
        });
      }
    });

    // Current folder (not a link)
    items.push({
      title: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 6, color: "var(--text-default-color, #333)" }}>
          <span
            className="pe-7s-folder"
            style={{
              fontSize: "var(--text-base)",
              color: activeFolder.color,
              WebkitTextStroke: "0.5px",
            }}
          />
          <span>{activeFolder.name}</span>
        </span>
      ),
    });

    return items;
  };

  const breadcrumbItems = buildBreadcrumbItems();

  const segmentedOptions = [
    {
      value: "grid",
      icon: <span className="pe-7s-keypad" style={SEG_ICON_STYLE} />,
    },
    {
      value: "list",
      icon: <span className="pe-7s-menu" style={SEG_ICON_STYLE} />,
    },
  ];

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        padding: "12px 0",
        gap: 8,
      }}
    >
      {/* Breadcrumb */}
      {showBreadcrumb ? (
        <div className="flex-1">
          <nav className="w-auto bg-transparent shadow-none">
            <TrebbleBreadcrumb
              useEditorTheme={false}
              items={breadcrumbItems}
            />
          </nav>
        </div>
      ) : (
        <div className="flex-1" />
      )}

      {/* Grid/List Toggle */}
      <div className="library-view-toggle">
        <TrebbleSegmented
          useEditorTheme={false}
          value={viewMode}
          options={segmentedOptions}
          onChange={handleViewModeChange}
        />
      </div>
    </div>
  );
};

export default LibraryToolbar;
